import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { RichTextEditor } from '@/components/rich-text-editor'
import { Badge } from "@/components/ui/badge"
import { BlogPost, BlogCategory, BlogTag } from '@/services/other-services'
import { useBlogCategories } from '@/hooks/use-blog-categories'
import { useBlogTags } from '@/hooks/use-blog-tags'
import api from '@/lib/axios'
import { Globe, X, Loader2, Image as ImageIcon, Trash2, Search } from 'lucide-react'
import { MediaPicker } from '@/components/media/media-picker'
import { getMediaUrl } from '@/utils/url-utils'
import { toast } from 'sonner'
import { useAuthStore } from '@/stores/authStore'
import { Check } from 'lucide-react'

const SUPPORTED_LANGUAGES = [
    { code: 'tr', name: 'Türkçe' },
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Español' },
    { code: 'ru', name: 'Русский' },
    { code: 'nl', name: 'Nederlands' },
    { code: 'ar', name: 'العربية' },
]

const localizedStringSchema = z.object({
    tr: z.string().min(2, { message: 'En az 2 karakter olmalıdır' }),
    en: z.string().optional().or(z.literal('')),
    fr: z.string().optional().or(z.literal('')),
    es: z.string().optional().or(z.literal('')),
    ru: z.string().optional().or(z.literal('')),
    nl: z.string().optional().or(z.literal('')),
    ar: z.string().optional().or(z.literal('')),
})

const optionalLocalizedStringSchema = z.object({
    tr: z.string().optional().or(z.literal('')),
    en: z.string().optional().or(z.literal('')),
    fr: z.string().optional().or(z.literal('')),
    es: z.string().optional().or(z.literal('')),
    ru: z.string().optional().or(z.literal('')),
    nl: z.string().optional().or(z.literal('')),
    ar: z.string().optional().or(z.literal('')),
})

const blogPostSchema = z.object({
    title: localizedStringSchema,
    slug: localizedStringSchema,
    content: localizedStringSchema,
    excerpt: optionalLocalizedStringSchema.optional(),
    categories: z.array(z.string()).min(1, { message: 'En az bir kategori seçilmelidir' }),
    tags: z.array(z.string()).optional(),
    featuredImage: z.string().optional().or(z.literal('')),
    isPublished: z.boolean().default(false),
    authorId: z.string().min(1, { message: 'Yazar seçilmelidir' }),
    readingTime: z.coerce.number().optional().default(0),
    seo: z.object({
        meta_title: optionalLocalizedStringSchema.optional(),
        meta_description: optionalLocalizedStringSchema.optional(),
        meta_keywords: optionalLocalizedStringSchema.optional(),
    }).optional(),
})

type BlogPostFormValues = z.infer<typeof blogPostSchema>

interface PostFormProps {
    post?: BlogPost | null
    onSubmit: (data: any) => Promise<void>
    onCancel: () => void
}

export function BlogPostForm({ post, onSubmit, onCancel }: PostFormProps) {
    const user = useAuthStore((s) => s.auth.user)
    const { data: categories } = useBlogCategories()
    const { data: tags } = useBlogTags()
    const [authors, setAuthors] = useState<any[]>([])
    const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false)

    const form = useForm<BlogPostFormValues>({
        resolver: zodResolver(blogPostSchema),
        defaultValues: {
            title: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
            slug: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
            content: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
            excerpt: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
            categories: [],
            tags: [],
            featuredImage: '',
            isPublished: false,
            authorId: user?.id || '',
            readingTime: 0,
            seo: {
                meta_title: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
                meta_description: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
                meta_keywords: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
            }
        }
    })

    useEffect(() => {
        const fetchAuthors = async () => {
            try {
                const res = await api.get('/blog/meta/authors')
                setAuthors(res.data)
            } catch (error) {
                console.error('Authors fetch error:', error)
            }
        }
        fetchAuthors()
    }, [])

    useEffect(() => {
        if (post && authors.length > 0) {
            const authorId = typeof post.authorId === 'object' ? post.authorId._id || post.authorId.id : post.authorId
            form.reset({
                title: post.title,
                slug: post.slug,
                content: post.content,
                excerpt: post.excerpt || { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
                categories: Array.isArray(post.categories) ? post.categories.map(c => typeof c === 'object' ? (c as BlogCategory)._id || (c as BlogCategory).id : (c as string)) as string[] : [],
                tags: Array.isArray(post.tags) ? post.tags.map(t => typeof t === 'object' ? (t as BlogTag)._id || (t as BlogTag).id : (t as string)) as string[] : [],
                featuredImage: post.featuredImage || '',
                isPublished: post.isPublished,
                authorId: authorId,
                readingTime: post.readingTime || 0,
                seo: post.seo || {
                    meta_title: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
                    meta_description: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
                    meta_keywords: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
                }
            })
        } else if (!post && user?.id) {
            form.setValue('authorId', user.id)
        }
    }, [post, form, authors, user])

    const generateSlug = (text: string) => {
        return text
            .trim()
            .replace(/İ/g, 'i')
            .replace(/I/g, 'i')
            .replace(/ı/g, 'i')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/ğ/g, 'g')
            .replace(/ü/g, 'u')
            .replace(/ş/g, 's')
            .replace(/ö/g, 'o')
            .replace(/ç/g, 'c')
            .replace(/[^a-z0-9-]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-+|-+$/g, '');
    }

    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        form.setValue('title.tr', value)
        if (!post) {
            form.setValue('slug.tr', generateSlug(value))
        }
    }

    const onFormSubmit = async (values: BlogPostFormValues) => {
        await onSubmit(values)
    }

    const toggleCategory = (categoryId: string) => {
        const currentCats = form.getValues('categories') || []
        const newCats = currentCats.includes(categoryId)
            ? currentCats.filter(id => id !== categoryId)
            : [...currentCats, categoryId]
        form.setValue('categories', newCats)
    }

    const toggleTag = (tagId: string) => {
        const currentTags = form.getValues('tags') || []
        const newTags = currentTags.includes(tagId)
            ? currentTags.filter(id => id !== tagId)
            : [...currentTags, tagId]
        form.setValue('tags', newTags)
    }

    return (
        <Form {...form}>
            <form
                onSubmit={form.handleSubmit(onFormSubmit, (errors) => {
                    console.error('Form Validation Errors:', errors)
                    toast.error('Lütfen tüm zorunlu alanları doldurun.')
                })}
                className="space-y-6 py-4 max-h-[80vh] overflow-y-auto px-1"
            >
                <div className="relative w-full rounded-lg border px-4 py-3 text-sm flex items-start gap-3 bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300">
                    <Globe className="h-4 w-4 mt-0.5" />
                    <div>
                        <p className="font-medium mb-1 flex items-center gap-1">✨ Yapay Zeka Çevirisi Aktif</p>
                        Türkçe içerik (başlık, özet, içerik ve SEO alanları) baz alınarak tüm diller otomatik olarak çevrilecektir. Maddelerin çokluğuna ve içeriğin uzunluğuna göre bu işlem biraz vakit alabilir, lütfen işlem tamamlanana kadar bekleyiniz.
                    </div>
                </div>

                <Tabs defaultValue="tr" className="w-full">
                    <TabsList className="grid w-full grid-cols-4 lg:grid-cols-7 h-auto p-1 bg-muted/50">
                        {SUPPORTED_LANGUAGES.map((lang) => (
                            <TabsTrigger key={lang.code} value={lang.code} className="py-2 text-xs">
                                {lang.name}
                            </TabsTrigger>
                        ))}
                    </TabsList>

                    {SUPPORTED_LANGUAGES.map((lang) => (
                        <TabsContent key={lang.code} value={lang.code} className="space-y-4 pt-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <FormField
                                    control={form.control}
                                    name={`title.${lang.code}` as any}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Başlık ({lang.name})</FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    value={(field.value as string) || ''}
                                                    onChange={lang.code === 'tr' ? handleTitleChange : field.onChange}
                                                    placeholder={`${lang.name} başlık girin`}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name={`slug.${lang.code}` as any}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Slug ({lang.name})</FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    value={(field.value as string) || ''}
                                                    placeholder="seo-uyumlu-baglanti"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <FormField
                                control={form.control}
                                name={`excerpt.${lang.code}` as any}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Özet ({lang.name})</FormLabel>
                                        <FormControl>
                                            <Input
                                                {...field}
                                                value={(field.value as string) || ''}
                                                placeholder={`${lang.name} kısa özet girin`}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={form.control}
                                name={`content.${lang.code}` as any}
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>İçerik ({lang.name})</FormLabel>
                                        <FormControl>
                                            <RichTextEditor
                                                value={(field.value as string) || ''}
                                                onChange={field.onChange}
                                                placeholder={`${lang.name} blog içeriğini buraya yazın...`}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <div className="space-y-4 border rounded-md p-4 bg-muted/20 mt-4">
                                <h4 className="text-sm font-semibold flex items-center gap-2">
                                    <Search className="h-4 w-4 text-primary" />
                                    SEO Ayarları ({lang.name})
                                </h4>
                                <FormField
                                    control={form.control}
                                    name={`seo.meta_title.${lang.code}` as any}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Meta Başlık</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Boş bırakılırsa başlık kullanılır" {...field} value={field.value || ''} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name={`seo.meta_description.${lang.code}` as any}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Meta Açıklama</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Arama sonuçlarında görünecek açıklama" {...field} value={field.value || ''} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name={`seo.meta_keywords.${lang.code}` as any}
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Meta Anahtar Kelimeler</FormLabel>
                                            <FormControl>
                                                <Input placeholder="Virgülle ayırarak yazın (örn: blog, skg, inşaat)" {...field} value={field.value || ''} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </TabsContent>
                    ))}
                </Tabs>

                <div className="space-y-2">
                    <Label className="text-sm font-medium">Kategoriler</Label>
                    <div className="flex flex-wrap gap-2 p-3 border rounded-lg min-h-[50px] bg-background">
                        {categories?.map(cat => {
                            const isSelected = form.watch('categories')?.includes(cat.id)
                            return (
                                <Badge
                                    key={cat.id}
                                    variant={isSelected ? "default" : "outline"}
                                    className="cursor-pointer hover:opacity-80 transition-opacity flex items-center gap-1 py-1"
                                    onClick={() => toggleCategory(cat.id)}
                                >
                                    {cat.name.tr}
                                    {isSelected && <X className="h-3 w-3" />}
                                </Badge>
                            )
                        })}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 border rounded-lg bg-muted/20">
                    <FormField
                        control={form.control}
                        name="authorId"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Yazar</FormLabel>
                                <Select onValueChange={field.onChange} value={field.value} key={field.value}>
                                    <FormControl>
                                        <SelectTrigger>
                                            <SelectValue placeholder="Yazar seçin" />
                                        </SelectTrigger>
                                    </FormControl>
                                    <SelectContent>
                                        {authors.map(author => (
                                            <SelectItem key={author._id} value={author._id}>
                                                <div className="flex items-center justify-between w-full gap-2">
                                                    <span>{author.name}</span>
                                                    {field.value === author._id && <Check className="h-3 w-3 ml-2 text-primary" />}
                                                </div>
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>

                <div className="space-y-2">
                    <Label className="text-sm font-medium">Etiketler</Label>
                    <div className="flex flex-wrap gap-2 p-3 border rounded-lg min-h-[50px] bg-background">
                        {tags?.map(tag => {
                            const isSelected = form.watch('tags')?.includes(tag.id)
                            return (
                                <Badge
                                    key={tag.id}
                                    variant={isSelected ? "default" : "outline"}
                                    className="cursor-pointer hover:opacity-80 transition-opacity flex items-center gap-1 py-1"
                                    onClick={() => toggleTag(tag.id)}
                                >
                                    {tag.name.tr}
                                    {isSelected && <X className="h-3 w-3" />}
                                </Badge>
                            )
                        })}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <FormField
                        control={form.control}
                        name="featuredImage"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Öne Çıkan Görsel</FormLabel>
                                <FormControl>
                                    <div className="space-y-3">
                                        {field.value ? (
                                            <div className="relative aspect-video w-full max-w-[300px] rounded-lg border overflow-hidden group">
                                                <img
                                                    src={getMediaUrl(field.value)}
                                                    alt="Öne Çıkan Görsel"
                                                    className="w-full h-full object-cover"
                                                />
                                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                                    <Button
                                                        type="button"
                                                        variant="secondary"
                                                        size="sm"
                                                        onClick={() => setIsMediaPickerOpen(true)}
                                                    >
                                                        Değiştir
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        variant="destructive"
                                                        size="icon"
                                                        className="h-8 w-8"
                                                        onClick={() => field.onChange('')}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <Button
                                                type="button"
                                                variant="outline"
                                                className="w-full h-32 border-dashed flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-primary hover:border-primary transition-all"
                                                onClick={() => setIsMediaPickerOpen(true)}
                                            >
                                                <ImageIcon className="h-8 w-8" />
                                                <span>Görsel Seç</span>
                                            </Button>
                                        )}
                                        <MediaPicker
                                            open={isMediaPickerOpen}
                                            onOpenChange={setIsMediaPickerOpen}
                                            onSelect={(url) => field.onChange(url)}
                                        />
                                    </div>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="readingTime"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Okunma Süresi (Dakika)</FormLabel>
                                <FormControl>
                                    <Input {...field} type="number" placeholder="Örn: 5" />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>

                <div className="flex items-center space-x-2 p-4 border rounded-lg">
                    <FormField
                        control={form.control}
                        name="isPublished"
                        render={({ field }) => (
                            <FormItem className="flex items-center space-x-2 space-y-0">
                                <FormControl>
                                    <Switch
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                    />
                                </FormControl>
                                <FormLabel className="font-normal cursor-pointer">Yayınla (Yazı sitede görünür olacaktır)</FormLabel>
                            </FormItem>
                        )}
                    />
                </div>

                <div className="flex justify-end gap-3 pt-6 border-t font-medium">
                    <Button type="button" variant="outline" onClick={onCancel}>İptal</Button>
                    <Button type="submit" size="lg" disabled={form.formState.isSubmitting}>
                        {form.formState.isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {post ? 'Değişiklikleri Kaydet' : 'Yazıyı Yayınla'}
                    </Button>
                </div>
            </form>
        </Form>
    )
}
